import React from "react";

import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(theme => ({
  page: {
    width: "100%",
    minWidth: 0,
    minHeight: "100%",
    boxSizing: "border-box",

    display: "flex",
    flexDirection: "column",
    gap: 10,

    padding: "24px 14px 24px",

    color: theme.palette.text.primary,
    background:
      theme.palette.type === "dark"
        ? "#0B1220"
        : "#F4F7FB",

    overflowX: "hidden",

    [theme.breakpoints.down("sm")]: {
      gap: 9,
      padding: "18px 10px 22px"
    }
  },

  header: {
    width: "100%",
    minWidth: 0,
    marginBottom: 16,

    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,

    [theme.breakpoints.down("sm")]: {
      marginBottom: 14,
      gap: 12,
      flexDirection: "column"
    }
  },
  headerContent: {
    minWidth: 0,
    flex: 1
  },
  eyebrow: {
    marginBottom: 4,
    color: "#155EEF",
    fontSize: 11,
    fontWeight: 750,
    lineHeight: 1.2,
    letterSpacing: ".06em",
    textTransform: "uppercase"
  },

  titleRow: {
    minWidth: 0,
    display: "block"
  },
  titleIcon: {
    flexShrink: 0,
    width: 38,
    height: 38,

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: 11,
    color: "#155EEF",
    background: "rgba(21,94,239,.08)",
    border: "1px solid rgba(21,94,239,.13)",

    "& .MuiSvgIcon-root": {
      fontSize: 20
    }
  },

  title: {
    minWidth: 0,

    color: theme.palette.text.primary,

    fontSize: "clamp(21px, 2vw, 27px)",
    fontWeight: 800,
    lineHeight: 1.15,
    letterSpacing: "-.035em",

    [theme.breakpoints.down("sm")]: {
      fontSize: 22,
      lineHeight: 1.2
    }
  },
  subtitle: {
    maxWidth: 760,
    marginTop: 5,

    color: theme.palette.text.secondary,

    fontSize: 12.5,
    lineHeight: 1.45,

    [theme.breakpoints.down("sm")]: {
      marginTop: 6,
      fontSize: 12.5,
      lineHeight: 1.5
    }
  },
  actions: {
    flexShrink: 0,

    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",

    [theme.breakpoints.down("sm")]: {
      width: "100%",
      justifyContent: "flex-start",
      alignItems: "center",
      gap: 8
    }
  },
  button: {
    minHeight: 36,
    padding: "6px 13px",

    borderRadius: 9,
    boxShadow: "none",

    fontSize: 11.5,
    fontWeight: 700,
    lineHeight: 1.2,
    textTransform: "none",

    "&:hover": {
      boxShadow: "none"
    },

    [theme.breakpoints.down("sm")]: {
      minHeight: 40,
      padding: "8px 14px",
      fontSize: 12
    }
  },
  primaryButton: {
    color: "#FFFFFF",
    background: "linear-gradient(135deg,#155EEF,#0BA5EC)",

    "&:hover": {
      background: "linear-gradient(135deg,#004EEB,#0086C9)"
    }
  },

  secondaryButton: {
    color: "#155EEF",
    background: "#FFFFFF",
    border: "1px solid rgba(21,94,239,.25)",

    "&:hover": {
      background: "rgba(21,94,239,.04)"
    }
  },

  ghostButton: {
    color: theme.palette.text.secondary,
    background: "transparent",
    border: `1px solid ${theme.palette.divider}`,

    "&:hover": {
      background: theme.palette.action.hover
    }
  },

  card: {
    minWidth: 0,
    boxSizing: "border-box",
    padding: 16,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 8px 22px rgba(0,0,0,.16)"
        : "0 7px 22px rgba(15,23,42,.05)"
  },

  sectionHeader: {
    minWidth: 0,
    marginBottom: 13,

    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12
  },

  sectionTitle: {
    color: theme.palette.text.primary,
    fontSize: 13.5,
    fontWeight: 750,
    lineHeight: 1.25
  },

  sectionSubtitle: {
    marginTop: 3,
    color: theme.palette.text.secondary,
    fontSize: 11,
    lineHeight: 1.4
  },

  toolbar: {
    minWidth: 0,
    marginBottom: 13,
    padding: "10px 11px",

    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",

    borderRadius: 13,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow: "0 5px 16px rgba(15,23,42,.04)"
  },

  statsGrid: {
    minWidth: 0,
    marginBottom: 14,

    display: "grid",
    gridTemplateColumns: "repeat(4,minmax(0,1fr))",
    gap: 12,

    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2,minmax(0,1fr))"
    },

    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },

  statCard: {
    position: "relative",
    minWidth: 0,
    minHeight: 112,
    boxSizing: "border-box",
    padding: "13px 14px",

    display: "flex",
    flexDirection: "column",

    overflow: "hidden",
    borderRadius: 15,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow: "0 6px 18px rgba(15,23,42,.045)"
  },

  statIcon: {
    position: "absolute",
    top: 12,
    right: 12,

    width: 34,
    height: 34,

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: 10,

    "& .MuiSvgIcon-root": {
      fontSize: 19
    }
  },

  statLabel: {
    width: "calc(100% - 44px)",
    minHeight: 30,
    color: theme.palette.text.secondary,
    fontSize: 11.5,
    fontWeight: 700,
    lineHeight: 1.3
  },

  statValue: {
    minWidth: 0,
    marginTop: 8,

    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",

    color: theme.palette.text.primary,
    fontSize: "clamp(20px,1.8vw,27px)",
    fontWeight: 800,
    lineHeight: 1.05,
    letterSpacing: "-.035em"
  },

  statFooter: {
    minWidth: 0,
    marginTop: "auto",
    paddingTop: 8,

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6
  },

  statHint: {
    minWidth: 0,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    color: theme.palette.text.secondary,
    fontSize: 10.5
  },

  statBadge: {
    flexShrink: 0,
    padding: "3px 6px",
    borderRadius: 6,
    fontSize: 9.5,
    fontWeight: 700
  }
}));

export const KoraxPage = ({
  children,
  className = "",
  style
}) => {
  const classes = useStyles();

  return (
    <div
      className={`${classes.page} ${className}`}
      style={style}
    >
      {children}
    </div>
  );
};

export const KoraxPageHeader = ({
  title,
  subtitle,
  actions,
  className = ""
}) => {
  const classes = useStyles();

  return (
    <header className={`${classes.header} ${className}`}>
      <div className={classes.headerContent}>
        <div className={classes.titleRow}>
          <Typography className={classes.title}>
            {title}
          </Typography>
        </div>

        {subtitle && (
          <Typography className={classes.subtitle}>
            {subtitle}
          </Typography>
        )}
      </div>

      {actions && (
        <div className={classes.actions}>
          {actions}
        </div>
      )}
    </header>
  );
};

export const KoraxButton = ({
  variant = "secondary",
  className = "",
  children,
  ...props
}) => {
  const classes = useStyles();

  const variantClass =
    variant === "primary"
      ? classes.primaryButton
      : variant === "ghost"
      ? classes.ghostButton
      : classes.secondaryButton;

  return (
    <Button
      {...props}
      className={`${classes.button} ${variantClass} ${className}`}
    >
      {children}
    </Button>
  );
};

export const KoraxToolbar = ({
  children,
  className = "",
  style
}) => {
  const classes = useStyles();

  return (
    <div
      className={`${classes.toolbar} ${className}`}
      style={style}
    >
      {children}
    </div>
  );
};

export const KoraxCard = ({
  title,
  subtitle,
  actions,
  children,
  className = "",
  style
}) => {
  const classes = useStyles();

  return (
    <section
      className={`${classes.card} ${className}`}
      style={style}
    >
      {(title || subtitle || actions) && (
        <div className={classes.sectionHeader}>
          <div>
            {title && (
              <Typography className={classes.sectionTitle}>
                {title}
              </Typography>
            )}

            {subtitle && (
              <Typography className={classes.sectionSubtitle}>
                {subtitle}
              </Typography>
            )}
          </div>

          {actions}
        </div>
      )}

      {children}
    </section>
  );
};

export const KoraxStatsGrid = ({
  children,
  className = "",
  style
}) => {
  const classes = useStyles();

  return (
    <div
      className={`${classes.statsGrid} ${className}`}
      style={style}
    >
      {children}
    </div>
  );
};

export const KoraxStatCard = ({
  label,
  value,
  hint,
  badge,
  icon,
  color = "#155EEF",
  onClick,
  className = ""
}) => {
  const classes = useStyles();

  return (
    <div
      className={`${classes.statCard} ${className}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      style={{
        cursor: onClick ? "pointer" : "default"
      }}
    >
      {icon && (
        <div
          className={classes.statIcon}
          style={{
            color,
            background: `${color}12`,
            border: `1px solid ${color}28`
          }}
        >
          {icon}
        </div>
      )}

      <Typography className={classes.statLabel}>
        {label}
      </Typography>

      <Typography
        className={classes.statValue}
        title={String(value ?? "")}
      >
        {value}
      </Typography>

      <div className={classes.statFooter}>
        <Typography
          className={classes.statHint}
          title={String(hint ?? "")}
        >
          {hint}
        </Typography>

        {badge && (
          <span
            className={classes.statBadge}
            style={{
              color,
              background: `${color}10`
            }}
          >
            {badge}
          </span>
        )}
      </div>
    </div>
  );
};
